import React, { useId, useRef } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import type { CarouselNavigatePayload } from '@noths/polaris-design-system-candidate-components';
import {
  useCarouselNavigation,
  useEdgeVisibility,
} from '@noths/polaris-design-system-candidate-components';
import { IconButton } from '@noths/polaris-design-system-core-components';

import * as copy from '../constants/copy';
import * as styles from './CustomersPhotos.styles';

export interface CustomersPhoto {
  alt: string;
  /** The Feefo media id, unique within a review. */
  id: string;
  /** The review the photo was attached to, which is where a click leads. */
  reviewId: string;
  url: string;
}

export interface CustomersPhotosProps {
  /** Where a photo leads: the drawer opened on the review it belongs to. */
  hrefForReview: (reviewId: string) => string;
  /** Fires once per settled movement, whether by arrow or by swipe. */
  onNavigate?: (payload: CarouselNavigatePayload) => void;
  onPhotoClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    photo: CustomersPhoto,
    index: number,
  ) => void;
  onVisible?: () => void;
  photos: CustomersPhoto[];
}

const noop = () => undefined;

/**
 * The most recent customer photos on the product page, as a carousel of squares with
 * the same header and arrows as the product carousels. Each photo links to its review
 * in the drawer rather than opening a lightbox of its own: the review is what gives the
 * photo its meaning, and the drawer is where the whole review is read. The consumer
 * decides whether there is anything to show; a strip with no photos is not rendered at
 * all rather than rendered empty.
 */
export const CustomersPhotos = ({
  hrefForReview,
  onNavigate,
  onPhotoClick,
  onVisible,
  photos,
}: CustomersPhotosProps) => {
  const trackId = useId();
  const trackRef = useRef<HTMLUListElement>(null);
  const rootRef = useImpressionRef<HTMLElement>(onVisible ?? noop);
  const { isAtEnd, isAtStart } = useEdgeVisibility(trackRef, photos.length);
  const { scrollNext, scrollPrev } = useCarouselNavigation(trackRef, onNavigate);

  return (
    <section
      aria-labelledby={styles.HEADING_ID}
      aria-roledescription="carousel"
      css={styles.root}
      ref={rootRef}
    >
      <header css={styles.header}>
        <h2 css={styles.heading} id={styles.HEADING_ID}>
          {copy.HEADING}
        </h2>
        {/* Disabled rather than unmounted at the ends, so the header keeps its height. */}
        <div aria-label={copy.CONTROLS_LABEL} css={styles.controls} role="group">
          <IconButton
            aria-controls={trackId}
            aria-label={copy.PREVIOUS_LABEL}
            compact
            disabled={isAtStart}
            icon="actionBack"
            onClick={scrollPrev}
          />
          <IconButton
            aria-controls={trackId}
            aria-label={copy.NEXT_LABEL}
            compact
            disabled={isAtEnd}
            icon="actionForward"
            onClick={scrollNext}
          />
        </div>
      </header>
      <ul css={styles.track} data-testid="customers-photos-track" id={trackId} ref={trackRef}>
        {photos.map((photo, index) => (
          <li css={styles.item} key={`${photo.reviewId}-${photo.id}`}>
            <a
              css={styles.photoLink}
              href={hrefForReview(photo.reviewId)}
              onClick={(e) => onPhotoClick?.(e, photo, index)}
            >
              <img
                alt={photo.alt}
                css={styles.photo}
                decoding="async"
                loading="lazy"
                src={photo.url}
              />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

CustomersPhotos.displayName = 'CustomersPhotos';
